import React, { createContext, useReducer, useEffect } from "react";
import PropTypes from "prop-types";
import * as dayjs from "dayjs";
import { throttle } from "throttle-debounce";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import "dayjs/locale/en-ca";
import "dayjs/locale/fr-ca";
import EventEmitter from "eventemitter3";
import { CONFIGS, DEFAULTS } from "CONFIGS";
import Actions from "./actions";
import appReducer from "./reducer";
import { Logger, LoggerInstanceInit } from "../../utils";
import { RCLService } from "../../services";
import { SessionManager } from "../../session-manager/client";
import { BRANDS } from "../../configs";
import { Placeholder } from "../../components/Placeholder";

const AppContext = createContext();
const AppDispatchContext = createContext();

const useAppState = () => {
	const context = React.useContext(AppContext);
	if (context === undefined) {
		throw new Error("useAppState must be used within the AppProvider");
	}
	return context;
};

const useAppDispatch = () => {
	const context = React.useContext(AppDispatchContext);
	if (context === undefined) {
		throw new Error("useAppDispatch must be used within the AppProvider");
	}
	return context;
};

const useAppContext = () => [useAppState(), useAppDispatch()];

/* Initialize logger */
LoggerInstanceInit(CONFIGS.LOGGER.API);

/* Setup RCL */
const RCLServiceFetch = new RCLService(
	CONFIGS.DCIS,
	DEFAULTS.CONTENTFUL_SPACE_RCL,
	DEFAULTS.RCL_APP_CODE
);

const sovSimplifiedSearchBarQuery = "(max-width: 767.98px)";
const sovTabletQuery = "(min-width: 576px) and (max-width: 991.98px)";
const sovDesktopQuery = "(min-width: 992px)";

const AppProvider = ({ children, config }) => {
	const { matches: sovSimplifiedSearchBar } = window.matchMedia(sovSimplifiedSearchBarQuery);
	const { matches: sovTablet } = window.matchMedia(sovTabletQuery);
	const { matches: sovDesktop } = window.matchMedia(sovDesktopQuery);

	const [windowSize, setWindowSize] = React.useState({
		isMobileView: window.matchMedia(sovSimplifiedSearchBarQuery).matches,
		isTabletView: window.matchMedia(sovTabletQuery).matches,
		isDesktopView: window.matchMedia(sovDesktopQuery).matches,
	});

	const initialState = {
		...config,
		isLoading: true,
		sovSimplifiedSearchBar,
		sovTablet,
		sovDesktop,
		isSOVNewMobileDesign: config.newMobileDesign && window.matchMedia("(max-width: 768px)").matches && config.brand === BRANDS.SOV,
		getDictionary: key => key,
	};

	const [state, dispatch] = useReducer(appReducer, {
		...initialState,
	});

	React.useEffect(() => {
		const updateWindowSize = throttle(150, () => {
		  const newWindowSize = {
			isMobileView: window.matchMedia(sovSimplifiedSearchBarQuery).matches,
			isTabletView: window.matchMedia(sovTabletQuery).matches,
			isDesktopView: window.matchMedia(sovDesktopQuery).matches,
		  };
	  
		  setWindowSize(prevSize => {
			if (
			  prevSize.isMobileView !== newWindowSize.isMobileView ||
			  prevSize.isTabletView !== newWindowSize.isTabletView ||
			  prevSize.isDesktopView !== newWindowSize.isDesktopView
			) {
			  return newWindowSize;
			}
			return prevSize;
		  });
		});
	  
		window.addEventListener("resize", updateWindowSize);
	  
		return () => {
		  window.removeEventListener("resize", updateWindowSize);
		};
	}, []);

	useEffect(() => {
		if (config.brand.toLowerCase() === BRANDS.SOV.toLowerCase()) {
			dispatch({
				type: Actions.SET_SOV_MOBILE_VIEW,
				payload: windowSize.isMobileView && config.newMobileDesign,
			});

			dispatch({
				type: Actions.SET_SOV_TABLET_VIEW,
				payload: windowSize.isTabletView,
			});

			dispatch({
				type: Actions.SET_SOV_DESKTOP_VIEW,
				payload: windowSize.isDesktopView,
			});
		}

		/* Initialize localization for Day.js */
		// eslint-disable-next-line react/prop-types
		dayjs.locale(config.lang === "fr" ? "fr-ca" : "en-ca");
		dayjs.extend(isSameOrAfter);

		if (CONFIGS.LOGGER.ENABLE === "false") {
			Logger.pause();
		}

		// Update DCIS Dictionary on language change and on init (since language is set after init)
		dispatch({ type: Actions.SET_LOADING, payload: true });
		RCLServiceFetch.getContent({ lang: state.lang }).then(() => {
			dispatch({
				type: Actions.SET_DICTIONARY,
				payload: { RCLServiceFetch, lang: state.lang },
			});
			/* Setup Session Manager */
			new SessionManager({
				baseURL: process.env.FINAL_URL,
				container: config.appRoot,
			}).then(SessionManagerInstance => {
				dispatch({
					type: Actions.SET_SESSION_MANAGER,
					payload: SessionManagerInstance,
				});
				dispatch({ type: Actions.SET_LOADING, payload: false });
			});
		});

		state.emitter.on("override-defaults", settings => {
			const { packages, flights, cruises } = settings;
			if (packages) {
				state.emitter.emit("update-packages-defaults", packages ?? {});
			}
			if (flights) {
				state.emitter.emit("update-flights-defaults", flights ?? {});
			}
			if (cruises) {
				state.emitter.emit("update-cruises-defaults", cruises ?? {});
			}
		});

		return () => {
			// Unmount logger to stop sending logs once all logs are sent
			Logger.unmount();

			state.emitter.off("override-defaults");
		};
	}, [state.lang, state.emitter, config, windowSize]);

	const placeholderConfig = {
		sovDesktop: state?.sovDesktop,
		sovSimplifiedSearchBar: state?.sovSimplifiedSearchBar,
		sovTablet: state?.sovTablet,
		type: state?.type,
		mode: state?.mode,
		brand: state?.brand
	}

	if (state.isLoading) {
		return (
			<Placeholder config={placeholderConfig} />
		)
	}

	return (
		<AppContext.Provider value={state}>
			<AppDispatchContext.Provider value={dispatch}>
				{!state.isLoading && children}
			</AppDispatchContext.Provider>
		</AppContext.Provider>
	);
};

AppProvider.propTypes = {
	children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
	config: PropTypes.shape({
		lang: PropTypes.oneOf(["en", "fr"]),
		reactDOM: PropTypes.instanceOf(DocumentFragment),
		appRoot: PropTypes.any.isRequired,
		type: PropTypes.string,
		alias: PropTypes.string,
		codeAg: PropTypes.string,
		brand: PropTypes.string,
		newMobileDesign: PropTypes.bool,
		emitter: PropTypes.instanceOf(EventEmitter),
	}).isRequired,
};

export { AppProvider, useAppContext, AppContext };
